// src/app/core/store/claim-analytics/claim-analytics.effects.ts

import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { switchMap, map, catchError, withLatestFrom, filter } from 'rxjs/operators';

import { TranslateService } from '@ngx-translate/core';

import { ClaimAnalyticsApiService } from '../../services/claim-analytics-api.service';
import {
  CaProductActions,
  CaConfigActions,
  CaLookupActions,
  CaOverviewActions,
  CaTimeSeriesActions,
  CaBreakdownActions,
  CaRankingsActions,
  CaComparisonActions,
} from './claim-analytics.actions';
import { selectCaConfigs, selectCascoCausesLoaded, selectHealthBenefitTypesLoaded } from './claim-analytics.selectors';

@Injectable()
export class ClaimAnalyticsEffects {
  private readonly actions$   = inject(Actions);
  private readonly api        = inject(ClaimAnalyticsApiService);
  private readonly store      = inject(Store);
  private readonly translate  = inject(TranslateService);

  private readError(err: unknown): string {
    const e = err as any;
    return e?.error?.message ?? e?.message ?? this.translate.instant('ERRORS.UNEXPECTED_DOT');
  }

  // ── Products ──
  loadProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaProductActions.loadProducts),
      switchMap(() =>
        this.api.getProducts().pipe(
          map(products => CaProductActions.loadProductsSuccess({ products })),
          catchError(err => of(CaProductActions.loadProductsFailure({ error: this.readError(err) }))),
        )
      ),
    )
  );

  autoSelectProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaProductActions.loadProductsSuccess),
      filter(({ products }) => products.length > 0),
      map(({ products }) => CaProductActions.selectProduct({ productTypeId: products[0].productTypeId })),
    )
  );

  // ── Config ──
  loadConfigOnSelect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaProductActions.selectProduct),
      withLatestFrom(this.store.select(selectCaConfigs)),
      filter(([{ productTypeId }, configs]) => !configs[productTypeId]),
      map(([{ productTypeId }]) => CaConfigActions.loadConfig({ productTypeId })),
    )
  );

  loadConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaConfigActions.loadConfig),
      switchMap(({ productTypeId }) =>
        this.api.getConfig(productTypeId).pipe(
          map(config => CaConfigActions.loadConfigSuccess({ productTypeId, config })),
          catchError(err => of(CaConfigActions.loadConfigFailure({ error: this.readError(err) }))),
        )
      ),
    )
  );

  // ── Lookups ──
  loadCascoCauses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaLookupActions.loadCascoCauses),
      withLatestFrom(this.store.select(selectCascoCausesLoaded)),
      filter(([, loaded]) => !loaded),
      switchMap(() =>
        this.api.getCascoCauses().pipe(
          map(causes => CaLookupActions.loadCascoCausesSuccess({ causes })),
        )
      ),
    )
  );

  loadHealthBenefitTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaLookupActions.loadHealthBenefitTypes),
      withLatestFrom(this.store.select(selectHealthBenefitTypesLoaded)),
      filter(([, loaded]) => !loaded),
      switchMap(() =>
        this.api.getHealthBenefitTypes().pipe(
          map(types => CaLookupActions.loadHealthBenefitTypesSuccess({ types })),
        )
      ),
    )
  );

  // ── Overview ──
  loadOverview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaOverviewActions.load),
      switchMap(({ request }) =>
        this.api.getOverview(request).pipe(
          map(data => CaOverviewActions.success({ data })),
          catchError(err => of(CaOverviewActions.failure({ error: this.readError(err) }))),
        )
      ),
    )
  );

  // ── TimeSeries ──
  loadTimeSeries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaTimeSeriesActions.load),
      switchMap(({ request }) =>
        this.api.getTimeSeries(request).pipe(
          map(data => CaTimeSeriesActions.success({ data })),
          catchError(err => of(CaTimeSeriesActions.failure({ error: this.readError(err) }))),
        )
      ),
    )
  );

  // ── Breakdown ──
  loadBreakdown$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaBreakdownActions.load),
      switchMap(({ request }) =>
        this.api.getBreakdown(request).pipe(
          map(data => CaBreakdownActions.success({ data })),
          catchError(err => of(CaBreakdownActions.failure({ error: this.readError(err) }))),
        )
      ),
    )
  );

  // ── Rankings ──
  loadRankings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaRankingsActions.load),
      switchMap(({ request }) =>
        this.api.getRankings(request).pipe(
          map(data => CaRankingsActions.success({ data })),
          catchError(err => of(CaRankingsActions.failure({ error: this.readError(err) }))),
        )
      ),
    )
  );

  // ── Comparison ──
  loadComparison$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaComparisonActions.load),
      switchMap(({ request }) =>
        this.api.getComparison(request).pipe(
          map(data => CaComparisonActions.success({ data })),
          catchError(err => of(CaComparisonActions.failure({ error: this.readError(err) }))),
        )
      ),
    )
  );
}
